/**
 * Date rules for a person's load on the Timeline ("Carga de Equipo").
 *
 * Mirrors the backend's shared rules (app/api/domain/models/User/load_rules.py),
 * which the matrix, the load history, the profile flags and the recommender use:
 *   - a row with no start and no end is not schedulable and never counts;
 *   - start only = open-ended (it runs until someone sets an end);
 *   - end only = active up to that end.
 * The page used to turn a start-only row into a one-day bar, so a running
 * start-only oferta showed 0% here while every other page showed its load.
 */

/** Stand-in end for open-ended rows (the T&M sentinel used across the app). */
export const OPEN_END_ISO = '2099-12-31'
/** Stand-in start for end-only rows; any date before the visible range works. */
export const OPEN_START_ISO = '1970-01-01'

const PRACTICAS_NAME = 'desarrollo y formación'

/** The interns' synthetic 'Desarrollo y formación' slot: listed, never counted as load. */
export const isPracticas = (name: string | null | undefined): boolean =>
    (name || '').trim().toLowerCase() === PRACTICAS_NAME

const toDay = (value: string): Date | null => {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return null
    d.setHours(0, 0, 0, 0)
    return d
}

export interface LoadWindow {
    start: Date
    end: Date
}

/** Resolve a row's [start, end] with the open-ended rules, or null when it has no dates. */
export const resolveWindow = (
    startStr: string | null | undefined,
    endStr: string | null | undefined,
): LoadWindow | null => {
    if (!startStr && !endStr) return null
    const start = toDay(startStr || OPEN_START_ISO)
    const end = toDay(endStr || OPEN_END_ISO)
    if (!start || !end) return null
    return { start, end }
}

/** Whether the window overlaps [rangeStart, rangeEnd] (both inclusive, day precision). */
export const windowOverlaps = (window: LoadWindow | null, rangeStart: Date, rangeEnd: Date): boolean =>
    !!window && window.start <= rangeEnd && window.end >= rangeStart
